import React, { useState, useEffect } from "react";
import { Clock, Calendar, Users, User, X } from "lucide-react";
import Modal from "react-modal";
import PlansModal from "./PlansModal";
import { useAuth } from "../context/AuthContext";
import { doc, updateDoc, arrayUnion, getDoc, increment } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { toast } from "react-toastify";
import { useClassBooking } from "../hooks/useClassBooking";
import { useTranslation } from "react-i18next";
import { autoJoinGroupFromClass } from "../utils/autoJoinGroupFromClass";

Modal.setAppElement("#root");

const useClassEnrollment = () => {
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [error, setError] = useState(null);
  const { user, setUser } = useAuth();

  // Helper function to update session storage
  const updateSessionStorage = (newEnrolledClass) => {
    const storedUser = JSON.parse(sessionStorage.getItem("user"));
    if (storedUser) {
      const updatedUser = {
        ...storedUser,
        enrolledClasses: [
          ...(storedUser.enrolledClasses || []),
          newEnrolledClass,
        ],
      };
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const enrollInClass = async (classId, userId, tutorId) => {
    setIsEnrolling(true);
    setError(null);
    try {
      // Get references to both documents
      const classRef = doc(db, "classes", classId);
      const userRef = doc(db, "students", userId);
      const tutorRef = doc(db, "tutors", tutorId);

      // Get the current class document to check available spots
      const classDoc = await getDoc(classRef);
      const classData = classDoc.data();

      if (!classData) {
        throw new Error("Class not found");
      }

      // Calculate total capacity and check if class is full
      const totalCapacity = 
        typeof classData.availableSpots !== "undefined"
          ? classData.availableSpots + (classData.classMemberIds?.length || 0)
          : undefined;
      const isFull =
        typeof totalCapacity !== "undefined" &&
        (classData.classMemberIds?.length || 0) >= totalCapacity;
      
      if (isFull) {
        throw new Error("Class is full");
      }

      // Update both documents in parallel
      // Use increment(-1) for atomic updates to prevent race conditions
      await Promise.all([
        updateDoc(classRef, {
          classMemberIds: arrayUnion(userId),
          availableSpots: increment(-1), // Atomically decrement available spots
        }),
        updateDoc(userRef, {
          enrolledClasses: arrayUnion(classId),
        }),
      ]);

      // Check if the adminId corresponds to an actual tutor or student document
      // For free group classes, the admin might be a student, not a tutor
      const tutorDoc = await getDoc(tutorRef);
      if (tutorDoc.exists()) {
        // Admin is a tutor
        await updateDoc(tutorRef, {
          tutorStudentIds: arrayUnion(userId),
        });
      } else {
        // Check if admin is a student (for free group classes)
        const studentAdminRef = doc(db, "students", tutorId);
        const studentAdminDoc = await getDoc(studentAdminRef);
        if (studentAdminDoc.exists()) {
          await updateDoc(studentAdminRef, {
            adminOfClasses: arrayUnion(classId),
          });
        }
        // If neither tutor nor student document exists, skip the admin update
        // This prevents the "no document to update" error
      }

      // Update context and session storage
      if (user) {
        const updatedUser = {
          ...user,
          enrolledClasses: [...(user.enrolledClasses || []), classId],
        };
        setUser(updatedUser);
        updateSessionStorage(classId);
      }

      // Automatically add user to group if class belongs to a group
      if (user && setUser) {
        try {
          await autoJoinGroupFromClass(classId, userId, user, setUser);
        } catch (groupJoinError) {
          // Don't fail class enrollment if group join fails
          console.warn(
            "[ProfileSetupClassCard] Failed to auto-join group:",
            groupJoinError,
          );
        }
      }

      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setIsEnrolling(false);
    }
  };

  return {
    enrollInClass,
    isEnrolling,
    error,
  };
};

const ProfileSetupClassCard = ({
  classId,
  className,
  language,
  languageLevel,
  classDateTime,
  classType,
  classDuration,
  tutorId,
  tutorName,
  tutorImageUrl,
  classMemberIds = [],
  availableSpots,
  physicalClass,
  imageUrl,
  classDescription,
  adminId,
  adminName,
  adminImageUrl,
  groupId,
  recurrenceType,
  selectedRecurrenceType,
  recurrenceTypes,
  onBook,
  isBooked,
  setIsProcessingBooking,
}) => {
  const { user, setUser } = useAuth();
  const { enrollInClass, isEnrolling, error, setError } = useClassEnrollment();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBookingConfirmationOpen, setIsBookingConfirmationOpen] =
    useState(false);
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
  const { t } = useTranslation();

  // Sync user context with session storage on mount and when user changes
  // This ensures that if free trial was activated in another view (like groups),
  // the updated freeAccess flag is reflected here
  useEffect(() => {
    const syncUserFromSession = () => {
      try {
        const sessionUser = sessionStorage.getItem("user");
        if (sessionUser && user?.uid) {
          const parsedUser = JSON.parse(sessionUser);
          // Only update if session storage has freeAccess but context doesn't
          // and they're the same user (to avoid race conditions)
          if (
            parsedUser.uid === user.uid &&
            parsedUser.freeAccess &&
            !user.freeAccess
          ) {
            console.log("Syncing freeAccess from session storage to context");
            setUser(parsedUser);
          }
        }
      } catch (error) {
        console.error("Error syncing user from session storage:", error);
      }
    };

    syncUserFromSession();
  }, [user?.uid, user?.freeAccess, setUser]); // Check when user changes

  // Determine which user details to display based on isBammbuu flag
  const isPremium =
    classType === "Individual Premium" || classType === "Group Premium";

  const displayName = isPremium ? tutorName : adminName;
  const displayImage = isPremium ? tutorImageUrl : adminImageUrl;

  const formatTime = (timestamp) => {
    if (!timestamp) return "TBD";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Add a helper to get the day name
  const getDayName = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return "TBD";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString("en-US", { weekday: "long" });
  };

  // Update formatDate to use day name for recurring classes
  const formatDate = (timestamp) => {
    const types =
      typeof recurrenceTypes !== "undefined"
        ? recurrenceTypes
        : typeof recurrenceType !== "undefined"
          ? [recurrenceType]
          : [];
    if (
      types &&
      Array.isArray(types) &&
      types.length > 0 &&
      types[0] !== "One-time" &&
      types[0] !== "None"
    ) {
      return getDayName(timestamp);
    }
    if (!timestamp || !timestamp.seconds) return "TBD";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleBookClass = (e) => {
    if (selectedRecurrenceType && selectedRecurrenceType !== "None") {
      toast.error(
        "This class is currently full. Please check back later or explore other available classes.",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    } else {
      e.stopPropagation(); // Prevent event bubbling
      setIsBookingConfirmationOpen(true);
    }
  };
  console.warn("isPremium in1 ", user.freeAccess);
  const { handleClassBooking } = useClassBooking();

  const handleConfirmBooking = async () => {
    if (!user) {
      setError("Please log in to enroll in classes");
      return;
    }

    // For premium classes
    if (isPremium) {
      // If user has free trial already activated, book directly
      if (user.freeAccess) {
        console.warn("User has free trial activated, booking directly");
        const success = await enrollInClass(classId, user.uid, adminId);
        if (success) {
          setIsBookingConfirmationOpen(false);
          setIsModalOpen(false);
          if (onBook) {
            onBook(classId);
          }
          toast.success("Class booked successfully!");
        } else {
          toast.error("Failed to book class. Please try again.");
        }
        return;
      }

      // If user doesn't have free trial activated, show trial activation modal directly
      console.warn(
        "User doesn't have free trial activated, showing trial activation modal",
      );
      setIsBookingConfirmationOpen(false);
      setIsPlansModalOpen(true);
      return;
    }

    // For non-premium classes, use the regular booking flow
    await handleClassBooking(
      user,
      classType, // Make sure this is available in your component props
      user.subscriptions,
      user.credits,
      // Success callback
      () => {
        setIsBookingConfirmationOpen(false);
        setIsModalOpen(false);
        if (onBook) {
          onBook(classId);
        }
        // toast.success("Successfully enrolled in class!");
      },
      // Failure callback
      (errorMessage) => {
        // toast.error(errorMessage);
        if (
          errorMessage.includes("subscription") ||
          errorMessage.includes("credits")
        ) {
          setIsBookingConfirmationOpen(false);
          setIsPlansModalOpen(true);
        }
      },
      // Enrollment function
      () => enrollInClass(classId, user.uid, adminId),
    );
  };

  // Callback to retry booking after trial activation
  const handleTrialActivatedAndBook = async () => {
    // Set processing state immediately for seamless transition
    if (setIsProcessingBooking) {
      setIsProcessingBooking(true);
    }

    try {
      // Fetch the updated user data from Firestore to ensure we have freeAccess
      const userRef = doc(db, "students", user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        toast.error("Failed to verify account. Please try again.");
        if (setIsProcessingBooking) {
          setIsProcessingBooking(false);
        }
        return;
      }

      const freshUserData = userDoc.data();
      const updatedUser = {
        ...user,
        ...freshUserData,
        freeAccess: freshUserData.freeAccess,
        subscriptions: freshUserData.subscriptions || [],
      };

      // Update context with fresh user data
      setUser(updatedUser);

      // Update session storage with complete user data including freeAccess
      sessionStorage.setItem("user", JSON.stringify(updatedUser));

      // Minimal delay to ensure context propagates
      await new Promise((resolve) => setTimeout(resolve, 100));

      const success = await enrollInClass(classId, updatedUser.uid, adminId);
      if (success) {
        if (onBook) {
          onBook(classId);
        }

        // Show success message after everything is complete
        toast.success("All set! Your class has been booked successfully!");
      } else {
        toast.error("Failed to book class. Please try again.");
      }
    } catch (error) {
      console.error("Error booking class after trial:", error);
      toast.error("Failed to book class. Please try again.");
    } finally {
      // Clear processing state
      if (setIsProcessingBooking) {
        setIsProcessingBooking(false);
      }
    }
  };

  const [profileUrl, setProfileUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminProfile = async () => {
      if (!adminId) {
        setLoading(false);
        return;
      }

      try {
        // Try to fetch from tutors collection first
        let adminDoc = await getDoc(doc(db, "tutors", adminId));

        // If not found in tutors, try students collection
        if (!adminDoc.exists()) {
          adminDoc = await getDoc(doc(db, "students", adminId));
        }

        if (adminDoc.exists()) {
          const adminData = adminDoc.data();
          setProfileUrl(adminData.photoUrl || null);
        }
      } catch (error) {
        console.error("Error fetching admin profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminProfile();
  }, [adminId]);

  // Determine if the user has already booked this class
  const isAlreadyBooked =
    isBooked ||
    (user &&
      (classMemberIds.includes(user.uid) ||
        (user.enrolledClasses && user.enrolledClasses.includes(classId))));
  // Calculate total capacity and check if full
  // Total capacity = availableSpots (remaining) + classMemberIds.length (booked)
  const totalCapacity = 
    typeof availableSpots !== "undefined"
      ? availableSpots + (classMemberIds?.length || 0)
      : undefined;
  const isClassFull =
    typeof totalCapacity !== "undefined" &&
    (classMemberIds?.length || 0) >= totalCapacity;

  return (
    <>
      <div className="w-80 hover:cursor-pointer" role="button" tabIndex={0}>
        <div
          className={`flex h-full flex-col rounded-3xl bg-white p-2 ${
            isPremium ? "border border-[#14b82c]" : "border border-[#f2a105]"
          }`}
        >
          <div className="relative w-full">
            <img
              alt={className}
              src={imageUrl || "/images/default-class.png"}
              className="h-60 w-full rounded-b-3xl rounded-t-2xl object-cover"
            />
            {/* Community Free / Tutor-Led Tag - Top Right */}
            <div className="absolute right-2 top-2 z-10">
              <span
                className={`rounded-full px-3 py-2 text-sm font-medium ${
                  isPremium
                    ? "bg-[#FFBF00] text-black"
                    : "bg-[#fff885] text-[#042f0c]"
                }`}
              >
                {isPremium
                  ? t("profileSetup.classTags.tutorLedTrial", "Tutor-Led")
                  : t(
                      "profileSetup.classTags.communityFree",
                      "Community (Free)",
                    )}
              </span>
            </div>

            {/* Banner at bottom of image */}
            <div className="absolute bottom-0 left-0 right-0 space-y-1 rounded-b-2xl bg-[#B9F9C2BF]/75 backdrop-blur-sm">
              <div className="pl-1 pt-2">
                <h2 className="ml-2 truncate text-lg font-bold text-gray-800 sm:text-xl">
                  {className}
                </h2>
              </div>

              <div className="flex items-center justify-between">
                <div className="mb-2 ml-2 flex items-center space-x-2">
                  <img
                    src={
                      language === "English"
                        ? "/svgs/xs-us.svg"
                        : language === "Spanish"
                          ? "/svgs/xs-spain.svg"
                          : "/svgs/eng-spanish-xs.svg"
                    }
                    alt={language === "English" ? "US Flag" : "Spain Flag"}
                    className="h-4 w-4"
                  />
                  <span className="flex items-center">
                    <span className="text-sm text-[#042f0c]">{language}</span>
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {languageLevel && languageLevel !== "None" && (
                    <span className="mb-2 mr-2 rounded-full bg-[#fff885] px-2 py-1 text-xs sm:px-3 sm:text-sm">
                      {languageLevel}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full flex-1 flex-col justify-evenly px-1 py-4">
            <div className="flex w-full flex-col space-y-1.5">
              <div className="flex w-full flex-row items-center justify-between">
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-[#454545]">
                    {formatTime(classDateTime)} ({classDuration} min)
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-[#454545]">
                    {formatDate(classDateTime)}
                  </span>
                </div>
              </div>
              <div className="flex w-full flex-row items-center justify-between">
                <div className="flex items-center space-x-1">
                  {profileUrl ? (
                    <img
                      src={profileUrl}
                      alt={adminName}
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-4 w-4 text-gray-600" />
                  )}
                  <span className="text-sm text-[#454545]">
                    {adminName || "TBD"} (Instructor)
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-gray-600" />
                  {isPremium ? (
                    <span className="text-sm text-[#454545]"></span>
                  ) : (
                    <span className="text-sm text-[#454545]">
                      {classMemberIds.length}/
                      {availableSpots + classMemberIds.length}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-auto pt-3">
              {isAlreadyBooked || isClassFull ? (
                <button
                  className={`w-full rounded-full py-2 font-medium ${
                    isAlreadyBooked
                      ? "cursor-default bg-[#14b82c] text-black"
                      : "cursor-not-allowed border border-gray-400 bg-gray-200 text-gray-500"
                  }`}
                  disabled={isAlreadyBooked || isClassFull}
                  title={
                    isAlreadyBooked
                      ? isPremium
                        ? "Booked (Trial)"
                        : "Joined"
                      : t("exploreClassCard.labels.classFull")
                  }
                >
                  {isAlreadyBooked
                    ? isPremium
                      ? t(
                          "profileSetup.classButtons.bookedTrial",
                          "Booked (Trial)",
                        )
                      : t(
                          "profileSetup.classButtons.joinedFree",
                          "Joined (Free)",
                        )
                    : t("exploreClassCard.labels.classFull")}
                </button>
              ) : (
                <button
                  onClick={handleBookClass}
                  className="w-full rounded-full border border-black bg-[#14B82C] py-2 font-medium text-black"
                >
                  {isPremium
                    ? t(
                        "profileSetup.classButtons.bookFreeTrial",
                        "Book Free Trial",
                      )
                    : t("profileSetup.classButtons.joinFree", "Join Free")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        className="mx-auto mt-20 max-w-md rounded-3xl bg-white font-urbanist outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{
          content: {
            border: "none",
            padding: 0,
            maxWidth: "450px",
          },
        }}
      >
        <div className="">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-2xl font-semibold">Class Details</h2>
            <button
              onClick={() => setIsModalOpen(false)}
              className="rounded-full bg-gray-200 p-2 hover:bg-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Main content with yellow background */}
          <div
            className={`${
              isPremium ? "bg-[#e6fde9]" : "bg-yellow-50"
            } mx-4 rounded-2xl px-6 pb-6 pt-8`}
          >
            {/* Profile image */}
            <div className="flex flex-col items-center">
              <div className="mb-4 h-24 w-24">
                <img
                  src={imageUrl || "/images/panda.png"}
                  alt={className}
                  className="h-full w-full rounded-full object-cover"
                />
              </div>

              <h2 className="mb-3 text-2xl font-bold">{className}</h2>

              {/* Language badge */}
              <div className="mb-6 flex items-center gap-2">
                <div className="h-6 w-6">
                  <img
                    src="/images/panda.png"
                    alt={language}
                    className="h-full w-full rounded-full"
                  />
                </div>
                <span>{language}</span>
                <span className="rounded-full bg-[#fff885] px-2 py-0.5 text-sm">
                  {languageLevel}
                </span>
              </div>

              {/* Time, Date, Users row */}
              <div className="mb-6 flex justify-center gap-8 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(classDateTime)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(classDateTime)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>
                    {classMemberIds?.length || 0}/
                    {availableSpots + (classMemberIds?.length || 0)}
                  </span>
                </div>
              </div>

              {/* Description */}
              <p className="mb-6 text-center text-gray-600">
                {classDescription}
              </p>

              {/* Language Group */}
              {groupId && (
                <div className="w-full">
                  <h3 className="mb-3 text-center text-lg font-bold">
                    Language Group
                  </h3>
                  <div className="flex items-center gap-4 rounded-xl border border-[#97e3a2] bg-white p-4">
                    <img
                      src="/images/panda.png"
                      alt={`${language} flag`}
                      className="h-12 w-12 rounded-full"
                    />
                    <div className="flex-1">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="font-medium">{`${language} Learners`}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <img
                          src="/images/panda.png"
                          alt={language}
                          className="h-4 w-4 rounded-full"
                        />
                        <span>{language}</span>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <img
                            src={displayImage || "/images/panda.png"}
                            alt="Leader"
                            className="h-4 w-4 rounded-full"
                          />
                          <span>{displayName}</span>
                        </div>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>{classMemberIds.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4">
            <button
              onClick={handleBookClass}
              className="w-full rounded-full border border-black bg-[#ffbf00] py-3 font-medium text-black hover:bg-[#ffbf00]"
            >
              {isPremium
                ? t(
                    "profileSetup.classButtons.bookFreeTrial",
                    "Book Free Trial",
                  )
                : t("profileSetup.classButtons.joinFree", "Join Free")}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isBookingConfirmationOpen}
        onRequestClose={() => setIsBookingConfirmationOpen(false)}
        className="mx-auto mt-40 max-w-sm rounded-3xl bg-white p-6 font-urbanist outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{
          content: {
            border: "none",
            padding: "24px",
            maxWidth: "420px", // max-w-sm
          },
        }}
      >
        <div className="text-center">
          <h2 className="mb-4 text-xl font-semibold">
            Are you sure you want to book this class?
          </h2>
          {error && <p className="mb-4 text-red-600">{error}</p>}
          <p className="mb-6 text-gray-600">
            {classType === "Individual Premium"
              ? t(
                  "exploreClassCard.confirmBooking.descriptionPremiumIndividual",
                )
              : t("exploreClassCard.confirmBooking.description")}
          </p>
          <div className="flex flex-row gap-2">
            <button
              onClick={() => setIsBookingConfirmationOpen(false)}
              className="w-full rounded-full border border-gray-300 py-2 font-medium hover:bg-gray-50"
            >
              No, Cancel
            </button>
            <button
              onClick={handleConfirmBooking}
              className="w-full rounded-full border border-[#042f0c] bg-[#14b82c] py-2 font-medium text-black hover:bg-[#119924]"
            >
              {isEnrolling ? "Enrolling..." : "Yes, Book Now"}
            </button>
          </div>
        </div>
      </Modal>
      <PlansModal
        isOpen={isPlansModalOpen}
        onClose={() => setIsPlansModalOpen(false)}
        initialTab="offers"
        onTrialActivated={handleTrialActivatedAndBook}
        directFreeTrial={true}
      />
    </>
  );
};

export default ProfileSetupClassCard;
